// frontend/src/contexts/WorkspaceContext.js
//
// Current-workspace state for the whole app.
//
// Every write now names a workspace (option A): the client states where content
// goes, the server validates membership. The server deliberately does NOT infer
// it -- a user may belong to several workspaces, and guessing would eventually
// file a conversation in the wrong one, which in this app is a privacy failure
// rather than a filing error.
//
// So "which workspace am I in" is explicit UI state, and it lives here.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);

// Remembering the last workspace per user avoids landing people in the wrong
// one after a reload. Keyed by user id so two accounts on one browser do not
// inherit each other's selection.
const storageKey = (userId) => `ai-chat.workspace.${userId}`;

export const WorkspaceProvider = ({ children }) => {
    const { user } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadWorkspaces = useCallback(async () => {
        if (!user) {
            setWorkspaces([]);
            setCurrentWorkspaceId(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetchWithAuth('/api/workspaces', {});
            if (!response.ok) throw new Error('Failed to load workspaces');

            const data = await response.json();
            setWorkspaces(data);
            setError(null);

            // Restore the previous selection when it is still valid, otherwise
            // fall back to the first workspace. Validating against the fetched
            // list matters: membership can be revoked between sessions, and a
            // stale id would make every write fail with a 403.
            const remembered = window.localStorage.getItem(storageKey(user.id));
            const rememberedId = remembered ? parseInt(remembered, 10) : null;
            const isValid = data.some((w) => w.id === rememberedId);

            setCurrentWorkspaceId(isValid ? rememberedId : (data[0]?.id ?? null));
        } catch (err) {
            console.error('Error loading workspaces:', err);
            setError(err.message);
            setWorkspaces([]);
            setCurrentWorkspaceId(null);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadWorkspaces();
    }, [loadWorkspaces]);

    const selectWorkspace = useCallback((workspaceId) => {
        setCurrentWorkspaceId(workspaceId);
        if (user && workspaceId != null) {
            window.localStorage.setItem(storageKey(user.id), String(workspaceId));
        }
    }, [user]);

    const currentWorkspace =
        workspaces.find((w) => w.id === currentWorkspaceId) || null;

    return (
        <WorkspaceContext.Provider
            value={{
                workspaces,
                currentWorkspace,
                currentWorkspaceId,
                selectWorkspace,
                loading,
                error,
                reload: loadWorkspaces,
            }}
        >
            {children}
        </WorkspaceContext.Provider>
    );
};

export const useWorkspace = () => {
    const context = useContext(WorkspaceContext);
    if (!context) {
        throw new Error('useWorkspace must be used within WorkspaceProvider');
    }
    return context;
};
